/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import type React from 'react';

import { type PolymorphicCommonProps } from '../_common/types';
import { ConditionalWrap } from '../conditional-wrap';

type SkeletonProps = {
  /** Shape of the skeleton */
  shape?: 'circular' | 'rectangular';
  /** Height of the skeleton */
  height?: string;
  /** Width of the skeleton */
  width?: string;
  /** Content displayed inside the skeleton. Size is inherited from the children if height or width are not provided. */
  children?: React.ReactNode;
  /** What background the skeleton is placed on. */
  onBackground?: 'weak' | 'default';
  /** Whether the skeleton is loading */
  isLoading?: boolean;
};

export const Skeleton = <T extends React.ElementType = 'div'>({
  as,
  shape = 'rectangular',
  className,
  style,
  height,
  width,
  isLoading = true,
  children,
  htmlAttributes,
  onBackground = 'default',
  ref,
  ...restProps
}: PolymorphicCommonProps<T, SkeletonProps>) => {
  const Component: React.ElementType = as ?? 'div';
  const classes = classNames(
    `ndl-skeleton ndl-skeleton-${shape}`,
    onBackground && `ndl-skeleton-${onBackground}`,
    className,
  );

  return (
    <ConditionalWrap
      shouldWrap={isLoading}
      wrap={(children) => (
        <Component
          ref={ref}
          className={classes}
          style={{
            ...style,
            height,
            width,
          }}
          aria-busy={true}
          tabIndex={-1}
          {...restProps}
          {...htmlAttributes}
        >
          <div
            aria-hidden={isLoading}
            className="ndl-skeleton-content"
            tabIndex={-1}
          >
            {children}
          </div>
        </Component>
      )}
    >
      {children}
    </ConditionalWrap>
  );
};

Skeleton.displayName = 'Skeleton';
